/**
 * A basic event emitter.
 *
 * Supports the emitting and listening for events.
 * When an event is set to bubble, if the listening target is also
 * and EventEmitter the Event is fired up the chain allowing deeply nested
 * objects to communicate with objects much higher up the hierarchy.
 *
 * @export
 * @class EventEmitter
 */
export class EventEmitter {
    constructor() {
        this._eventListeners = {};
    }

    /**
     * Register an event handler for a specific event
     *
     * @function on
     * @param {String} evt - String name of the event
     * @param {Function} listener - The function to perform when the event occurs
     * @param {any} target - The object to apply the listener to.
     */
    on(evt, listener, target = null) {
        if (!this._eventListeners.hasOwnProperty(evt)) {
            this._eventListeners[evt] = [];
        }
        this._eventListeners[evt].push({
            handler: listener,
            target: target || null,
        });
    }

    /**
     * Remove an existing listener
     *
     * @function removeEventListener
     * @param {String} evt - String name of the event
     * @param {Function} listener - The function to perform when the event occurs
     */
    removeEventListener(evt, listener) {
        if (!this._eventListeners.hasOwnProperty(evt)) {
            return;
        }

        for (const i in this._eventListeners[evt]) {
            if (this._eventListeners[evt][i].handler === listener) {
                this._eventListeners[evt].splice(i, 1);
                return;
            }
        }
    }

    /**
     * Emit an event.
     *
     * All listeners registered will fire in the order they were added.
     *
     * If the target of the listener is an instance of EventEmitter and
     * the event is set to bubble the target emit method is called forcing
     * bubbling up the hierarchy.
     *
     * @function emit
     * @param {FrameworkEvent} evt - The event instance to fire
     */
    emit(evt) {
        if (!this._eventListeners.hasOwnProperty(evt.name)) {
            return;
        }

        this._eventListeners[evt.name].map((listener) => {
            listener.handler.apply(listener.target, [evt]);

            if ((listener.target instanceof EventEmitter) && evt.bubbles) {
                listener.target.emit(evt.clone(listener.target));
            }
        });
    }


    /**
     * Test if emitter has an listener.
     *
     * Comparison is made on the handler through strict equivalence. Arrow
     * and anonymous functions will not be equivalent.
     *
     * @param {String} evt
     * @param {Function} listener
     */
    hasListener(evt, listener) {
        for (const i in this._eventListeners[evt]) {
            if (this._eventListeners[evt][i].handler === listener) {
                return true;
            }
        }
        return false;
    }
}
